import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { DailyUserMetricsData } from "@/types/metrics"
import { useMemo } from "react"

interface UserMetricsTableProps {
  dailyMetrics: DailyUserMetricsData[]
  monthlyMetrics: DailyUserMetricsData[]
}

export const UserMetricsTable = ({
  dailyMetrics,
  monthlyMetrics,
}: UserMetricsTableProps) => {
  const { t } = useTranslation()

  // Sort daily metrics by date (newest first)
  const sortedDailyMetrics = useMemo(() => {
    return [...dailyMetrics].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
  }, [dailyMetrics])

  // Calculate date range for display
  const dateRange = useMemo(() => {
    if (dailyMetrics.length === 0) {
      return null
    }

    const dates = dailyMetrics.map((m) => new Date(m.date).getTime())
    const startDate = new Date(Math.min(...dates))
    const endDate = new Date(Math.max(...dates))

    return {
      start: format(startDate, "yyyy-MM-dd"),
      end: format(endDate, "yyyy-MM-dd"),
    }
  }, [dailyMetrics])

  // Common table header and cell classes for consistency
  const tableHeaderClass =
    "h-10 px-2 text-left align-middle font-medium text-muted-foreground bg-muted/50"
  const tableHeaderRightClass =
    "h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-muted/50"
  const tableCellClass = "p-2 align-middle"
  const tableCellRightClass = "p-2 text-right align-middle"

  return (
    <div className="space-y-8">
      {/* Daily Metrics Table */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t("metrics.user.daily.title")}
          </h3>
          {dateRange && (
            <div className="text-sm text-muted-foreground">
              {t("metrics.dateRangeDescription", {
                startDate: dateRange.start,
                endDate: dateRange.end,
              })}
            </div>
          )}
        </div>

        <div className="border rounded-md shadow-xs overflow-hidden">
          <div className="relative">
            <table className="w-full">
              <thead>
                <tr>
                  <th style={{ width: "120px" }} className={tableHeaderClass}>
                    {t("metrics.date")}
                  </th>
                  <th
                    style={{ width: "120px" }}
                    className={tableHeaderRightClass}
                  >
                    {t("metrics.user.conversations")}
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <div className="max-h-[30vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            <table className="w-full">
              <tbody>
                {sortedDailyMetrics.map((metric, index) => (
                  <tr
                    key={metric.date}
                    className={`border-t border-gray-100 dark:border-gray-800 hover:bg-muted/30 transition-colors ${
                      index % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/30" : ""
                    }`}
                  >
                    <td style={{ width: "120px" }} className={tableCellClass}>
                      {format(new Date(metric.date), "yyyy-MM-dd")}
                    </td>
                    <td
                      style={{ width: "120px" }}
                      className={tableCellRightClass}
                    >
                      {metric.conversationsSubmitted}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly Aggregation Table */}
      <div>
        <h3 className="text-lg font-medium mb-4">
          {t("metrics.user.monthly.title")}
        </h3>
        <div className="border rounded-md shadow-xs overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className={tableHeaderClass}>{t("metrics.month")}</th>
                <th className={tableHeaderRightClass}>
                  {t("metrics.user.conversations")}
                </th>
              </tr>
            </thead>
            <tbody>
              {monthlyMetrics.map((metric, index) => (
                <tr
                  key={metric.date}
                  className={`border-t border-gray-100 dark:border-gray-800 hover:bg-muted/30 transition-colors ${
                    index % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/30" : ""
                  }`}
                >
                  <td className={tableCellClass}>
                    {format(new Date(metric.date), "yyyy-MM")}
                  </td>
                  <td className={tableCellRightClass}>
                    {metric.conversationsSubmitted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
